import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";

import { CloseIcon } from "@/icons";

export interface PublishProgressView {
  completed: number;
  total: number;
}

export interface PublishConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  acceptedCount: number;
  publishing?: boolean;
  /**
   * Live publish progress from the SSE stream. Present once the first frame
   * arrives; drives the determinate progress bar. Absent before the first frame
   * (the bar falls back to indeterminate while `publishing`).
   */
  progress?: PublishProgressView;
  /** A failed publish attempt; surfaced inline so the user can retry. */
  error?: string | null;
}

export function PublishConfirmationModal({
  open,
  onClose,
  onConfirm,
  acceptedCount,
  publishing = false,
  progress,
  error = null,
}: PublishConfirmationModalProps) {
  const assetLabel = acceptedCount === 1 ? "asset" : "assets";
  const hasProgress = progress !== undefined && progress.total > 0;
  const progressValue = hasProgress
    ? Math.min(100, Math.round((progress.completed / progress.total) * 100))
    : 0;

  return (
    <Dialog
      open={open}
      onClose={publishing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="h5" component="span" fontWeight={700}>
            Publish the collection
          </Typography>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={onClose}
            disabled={publishing}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          You're about to publish{" "}
          <Typography component="span" fontWeight={700} color="text.primary">
            {acceptedCount} accepted {assetLabel}
          </Typography>
          . This releases them for the collection and completes the generation.
          Anything you removed won't be published.
        </Typography>

        {publishing && (
          <Box sx={{ mt: 4 }} data-testid="publish-progress">
            <LinearProgress
              variant={hasProgress ? "determinate" : "indeterminate"}
              value={progressValue}
              aria-label="Publish progress"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
              data-testid="publish-progress-label"
            >
              {hasProgress
                ? `Publishing ${progress.completed} of ${progress.total}…`
                : "Publishing…"}
            </Typography>
          </Box>
        )}

        {!publishing && error && (
          <Alert severity="error" sx={{ mt: 4 }} data-testid="publish-error">
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4, gap: 2 }}>
        <Button onClick={onClose} disabled={publishing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={publishing || acceptedCount === 0}
          data-testid="publish-confirm"
        >
          {publishing
            ? "Publishing…"
            : error
              ? `Retry publish (${acceptedCount} ${assetLabel})`
              : `Publish ${acceptedCount} ${assetLabel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
